import { extractPrograms } from "./programs.mjs";

// Per-page text from a catalog PDF, in reading order, using pdf.js. Shared by
// the browser (AiChatPanel extracts the open catalog live from S3) and
// scripts/ai-poc (eval), so both see exactly the same text.
//
// pdf.js returns text runs with positions but in content-stream order, which
// can put a heading above the previous program's table (Feather River p. 117)
// or interleave two columns (Fresno). So each page is laid out from positions:
// find a vertical gutter → read the left column, then the right; runs that
// span the gutter (full-width headings) act as section breaks.

// pdfjs: the pdf.js module (pdfjs-dist), passed in so the browser and Node can
// each load their own build. data: ArrayBuffer/Uint8Array of the PDF.
export async function extractPages(pdfjs, data, { onProgress } = {}) {
  const doc = await pdfjs.getDocument({
    data,
    // S3 refuses CORS preflights, so no range/stream requests: we already
    // hold the whole file anyway.
    disableRange: true,
    disableStream: true,
    isEvalSupported: false,
  }).promise;
  const pages = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const { width } = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    pages.push({ page: n, text: layoutPage(content.items, width) });
    page.cleanup();
    if (onProgress) onProgress(n, doc.numPages);
  }
  await doc.destroy();
  return pages;
}

function layoutPage(rawItems, pageWidth) {
  const items = rawItems
    .filter((it) => it.str && it.str.trim())
    .map((it) => ({
      str: it.str,
      x: it.transform[4],
      y: it.transform[5],
      w: it.width,
      size: Math.abs(it.transform[3]) || it.height || 10,
    }));
  if (!items.length) return "";

  const gutter = findGutter(items, pageWidth);
  if (gutter == null) return linesToText(items);

  // Walk top to bottom; a run crossing the gutter closes the current band.
  const spans = items.filter((it) => it.x < gutter && it.x + it.w > gutter).sort((a, b) => b.y - a.y);
  const cols = items.filter((it) => !(it.x < gutter && it.x + it.w > gutter));
  const out = [];
  let top = Infinity;
  for (const boundary of [...spans.map((s) => s.y), -Infinity]) {
    const band = cols.filter((it) => it.y < top && it.y >= boundary);
    out.push(linesToText(band.filter((it) => it.x + it.w / 2 < gutter)));
    out.push(linesToText(band.filter((it) => it.x + it.w / 2 >= gutter)));
    const spanLine = spans.filter((s) => s.y === boundary);
    if (spanLine.length) out.push(linesToText(spanLine));
    top = boundary;
  }
  return out.filter(Boolean).join("\n");
}

// A two-column page has a vertical strip near the middle that (almost) no text
// crosses, with substantial text on both sides. Returns its x, or null.
function findGutter(items, pageWidth) {
  // Fine bins: Fresno's gutter is ~1% of the page width.
  const BINS = 400;
  const cover = new Array(BINS).fill(0);
  for (const it of items) {
    const a = Math.max(0, Math.floor((it.x / pageWidth) * BINS));
    const b = Math.min(BINS - 1, Math.floor(((it.x + it.w) / pageWidth) * BINS));
    for (let i = a; i <= b; i++) cover[i]++;
  }
  const allowed = Math.max(1, Math.round(items.length * 0.04)); // a few spanning headings
  let best = null;
  for (let i = BINS * 0.3; i <= BINS * 0.7; i++) {
    if (cover[i] <= allowed && (!best || cover[i] < best.c)) best = { i, c: cover[i] };
  }
  if (!best) return null;
  const gutter = ((best.i + 0.5) / BINS) * pageWidth;
  // Real columns: plenty of text on both sides, and most lines on each side
  // sit right up against the gutter rather than far from it (unlike a table
  // whose middle column happens to be empty).
  const left = items.filter((it) => it.x + it.w / 2 < gutter);
  const right = items.filter((it) => it.x + it.w / 2 >= gutter);
  if (left.length < items.length * 0.2 || right.length < items.length * 0.2) return null;
  const near = pageWidth * 0.08;
  const leftReach = left.filter((it) => gutter - (it.x + it.w) < near).length / left.length;
  const rightStart = right.filter((it) => it.x - gutter < near).length / right.length;
  if (leftReach < 0.25 || rightStart < 0.25) return null;
  return gutter;
}

// Group runs into lines by baseline, read top to bottom and left to right.
function linesToText(items) {
  if (!items.length) return "";
  const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x);
  const lines = [];
  for (const it of sorted) {
    const line = lines[lines.length - 1];
    if (line && Math.abs(line.y - it.y) < Math.min(line.size, it.size) * 0.5) line.items.push(it);
    else lines.push({ y: it.y, size: it.size, items: [it] });
  }
  return lines
    .map((line) => {
      const runs = line.items.sort((a, b) => a.x - b.x);
      let text = "";
      let end = null;
      for (const r of runs) {
        if (end != null && r.x - end > r.size * 0.15 && !text.endsWith(" ")) text += " ";
        text += r.str;
        end = r.x + r.w;
      }
      return text.replace(/\s+/g, " ").trim();
    })
    .filter(Boolean)
    .join("\n");
}

// Everything answer.mjs's buildIndex() needs from a PDF.
export async function prepareCatalog(pdfjs, data, { name, onProgress } = {}) {
  const pages = await extractPages(pdfjs, data, { onProgress });
  return { name, pages, programs: extractPrograms(pages) };
}
